import { Alert, AlertDescription } from '@ifixit/ui/components/alert';
import { Dialog, DialogContent, DialogTitle } from '@ifixit/ui/components/dialog';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';
import { ButtonStyles } from '@ifixit/ui/components/button';
import { useMediaQuery } from '@ifixit/ui/hooks/use-media-query';

// Type definitions
interface StatusNotification {
   type: 'success' | 'info' | 'warning' | 'error';
   message: string;
}

interface UserAction {
   id: string;
   text: string;
   url: string;
}

interface MiscLink {
   text: string;
   url: string;
}

interface Team {
   name: string;
   url: string;
   avatar: string;
}

interface UserStats {
   reputation: number;
   answers: number;
   guides: number;
}

type ConditionalDisplaysType = {
   statusNotifications: StatusNotification[];
   userBio: string;
   miscLink?: MiscLink | null;
   actions: UserAction[];
   editUrl?: string;
};

export type UserSummaryType = {
   name: string;
   username: string;
   avatarLink: string;
   avatarSrc: string;
   stats: UserStats;
   dateJoined: string;
   teams: Team[];
   privilege: {
      icon: string;
   };
};

export function UserCard({
   summaryData,
   conditionalData,
   ...userData
}: {
   summaryData?: UserSummaryType;
   conditionalData?: ConditionalDisplaysType;
} & Partial<UserSummaryType>) {
   const data = summaryData || userData;
   const isMobile = useMediaQuery('(max-width: 767px)');

   return (
      <div className="flex flex-col flex-shrink-0 p-4 min-md:pt-5 bg-muted rounded-[20px] gap-4 self-start w-full">
         {isMobile ? <MobileUserSummary {...data} /> : <DesktopUserSummary {...data} />}
         {conditionalData && <ConditionalDisplays {...conditionalData} />}
      </div>
   );
}

const AvatarImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
   const { isOpen, onOpen, onClose } = useDisclosure();
   const largeImageSrc = src.replace('.standard', '.large');

   return (
      <>
         <img className={`cursor-pointer ${className}`} src={src} alt={alt} onClick={onOpen} />
         <Dialog open={isOpen} onOpenChange={open => (open ? onOpen() : onClose())}>
            <DialogContent className="max-w-xl border-none bg-transparent p-0 shadow-none [&>[data-slot=dialog-close]]:text-white [&>[data-slot=dialog-close]]:bg-black/60 [&>[data-slot=dialog-close]]:rounded-full [&>[data-slot=dialog-close]]:p-1 [&>[data-slot=dialog-close]]:opacity-100 [&>[data-slot=dialog-close]]:active:bg-black/80 [&>[data-slot=dialog-close]]:active:text-white [&>[data-slot=dialog-close]]:focus-visible:ring-offset-0">
               <DialogTitle className="sr-only">{alt}</DialogTitle>
               <img className="rounded-lg w-full h-auto" src={largeImageSrc} alt={alt} />
            </DialogContent>
         </Dialog>
      </>
   );
};

const MobileUserSummary = ({
   name = '',
   username = '',
   avatarSrc = '',
   stats = { reputation: 0, answers: 0, guides: 0 },
   teams = [],
   privilege = { icon: '' },
}: Partial<UserSummaryType> & { stats?: UserStats }) => (
   <>
      <div className="flex gap-2">
         <AvatarImage src={avatarSrc} alt={name} className="rounded-full size-[72px]" />
         <div className="flex flex-col">
            <NameHeading name={name} privilege={privilege} />
            {username && (
               <div className="text-gray-600 dark:text-muted-foreground text-sm">@{username}</div>
            )}
            {teams.length > 0 && <TeamAvatars teams={teams} />}
         </div>
      </div>
      <UserStats stats={stats} />
   </>
);

const DesktopUserSummary = ({
   name = '',
   username = '',
   avatarSrc = '',
   stats = { reputation: 0, answers: 0, guides: 0 },
   dateJoined = '',
   teams = [],
   privilege = { icon: '' },
}: Partial<UserSummaryType> & { stats?: UserStats }) => (
   <div className="flex min-md:flex-col gap-2 md:gap-4 max-md:items-center">
      <div className="flex items-end w-fit">
         <AvatarImage src={avatarSrc} alt={name} className="rounded-full size-[72px]" />
         {teams.length > 0 && <TeamAvatars teams={teams} />}
      </div>
      <div>
         <NameHeading name={name} privilege={privilege} />
         {username && (
            <div className="text-gray-600 dark:text-muted-foreground text-sm">@{username}</div>
         )}
      </div>
      <div className="max-md:hidden">
         <UserStats stats={stats} dateJoined={dateJoined} />
      </div>
   </div>
);

const NameHeading = ({ name, privilege }: { name: string; privilege: { icon: string } }) => {
   const icon = privilege.icon;
   return (
      <h1
         id="aboutBoxName"
         className="flex items-center gap-1.5 text-base font-semibold text-primary m-0 leading-none"
      >
         {name}
         {icon && <PrivilegeIcon icon={icon} />}
      </h1>
   );
};

const PrivilegeIcon = ({ icon }: { icon: string }) => (
   <span className="post-author-status">
      <FaIcon name={icon} className="size-[16px]" />
   </span>
);

const StatItem = ({
   label,
   value,
   id,
   className,
}: {
   label: string;
   value: string | number;
   id?: string;
   className?: string;
}) => (
   <div className={`flex gap-1 ${className ?? ''}`}>
      <span>{label}:</span>
      <span className="font-semibold" id={id}>
         {value}
      </span>
   </div>
);

const UserStats = ({ stats, dateJoined = '' }: { stats: UserStats; dateJoined?: string }) => {
   const t = useTranslations('UserPage');
   const { reputation = 0, answers = 0, guides = 0 } = stats;
   const statItems = [
      { label: t('reputation'), value: reputation, show: reputation !== 0, id: 'numReputation' },
      {
         label: t('memberSince'),
         value: dateJoined,
         show: !!dateJoined,
         className: 'max-md:hidden',
      },
      {
         label: t('solutions'),
         value: answers,
         show: answers > 0,
         className: 'max-md:hidden',
         id: 'numAnswers',
      },
      {
         label: t('guides'),
         value: guides,
         show: guides > 0,
         className: 'max-md:hidden',
         id: 'numGuides',
      },
   ];

   return (
      <div id="userStatList">
         {statItems.map(
            ({ label, value, show, id, className }) =>
               show && (
                  <StatItem key={label} label={label} value={value} id={id} className={className} />
               )
         )}
      </div>
   );
};

const ConditionalDisplays = ({
   statusNotifications = [],
   userBio = '',
   miscLink,
   actions = [],
   editUrl,
}: ConditionalDisplaysType) => {
   const showStatuses = statusNotifications.length > 0;
   const showUserBio = userBio.length > 0;
   const showActions = actions.length > 0;

   return (
      <div className="flex flex-col gap-4">
         {showStatuses && <StatusNotifications statuses={statusNotifications} />}
         {showUserBio && (
            <div
               className="userBio max-md:hidden [&_p]:m-0"
               dangerouslySetInnerHTML={{ __html: userBio }}
            />
         )}
         {miscLink && (
            <div className="max-md:hidden">
               <MiscLink {...miscLink} />
            </div>
         )}
         {showActions && (
            <div className="max-md:hidden">
               <Actions actions={actions} />
            </div>
         )}
         {editUrl && <EditProfileButton editUrl={editUrl} />}
      </div>
   );
};

const ALERT_VARIANTS = {
   success: { variant: 'success', icon: 'circle-check' },
   info: { variant: 'info', icon: 'circle-info' },
   warning: { variant: 'warning', icon: 'triangle-exclamation' },
   error: { variant: 'critical', icon: 'circle-exclamation' },
} as const;

const StatusNotifications = ({ statuses }: { statuses: StatusNotification[] }) => (
   <div>
      {statuses.map(status => {
         const { variant, icon } = ALERT_VARIANTS[status.type] ?? ALERT_VARIANTS.info;
         return (
            <Alert key={status.message} variant={variant} className="my-1 py-1">
               <FaIcon name={icon} />
               <AlertDescription>
                  <p className="m-0">{status.message}</p>
               </AlertDescription>
            </Alert>
         );
      })}
   </div>
);

const TeamAvatars = ({ teams = [] }: { teams: Team[] }) => (
   <div className="flex min-md:-ml-[18px] max-md:mt-auto">
      {/* Cap displayed team avatars at 7 to avoid overflow */}
      {teams.slice(0, 7).map(team => (
         <a href={team.url} key={team.name} className="not-first:ml-[-4px] md:not-first:ml-[-8px]">
            <img
               className="size-7 md:size-8 rounded-full outline-2 outline-muted"
               src={team.avatar}
               alt={team.name}
            />
         </a>
      ))}
   </div>
);

const MiscLink = ({ url, text }: { url: string; text: string }) => (
   <a className="flex items-start gap-1 text-primary py-0.5" href={url}>
      <FaIcon name="circle-info" className="translate-y-[3px]" />
      {text}
   </a>
);

const Actions = ({ actions }: { actions: UserAction[] }) => (
   <>
      {actions.map(action => (
         <a
            className="flex items-start gap-1 text-primary py-0.5"
            href={action.url}
            data-testid={action.id}
            key={action.text}
         >
            <FaIcon name="circle-info" className="translate-y-[3px]" />
            {action.text}
         </a>
      ))}
   </>
);

const EditProfileButton = ({ editUrl }: { editUrl: string }) => {
   const t = useTranslations('UserPage');
   return (
      <a href={editUrl} className={ButtonStyles({ variant: 'white' })}>
         <FaIcon name="pen-to-square" />
         <span>{t('editProfile')}</span>
      </a>
   );
};
